import { describeKnowledgeApiError } from 'utils/knowledgeErrors';
import { JustRightHeightModal } from 'components/styled/scoped';
import { useTranslate } from '@tolgee/react';
import { memo, ReactNode, useEffect, useMemo, useState } from 'react';
import { get } from 'lodash-es';
import { css } from '@emotion/css';
import { Knowledge, SpuClass } from './type';
import { getMultiLangValue } from '@knowledge/knowledges/singleClass/helpers';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { message, Switch } from 'antd';
import { useRequest } from 'ahooks';
import { updateKnowledgeStatus } from './api';

interface ClosedKnowledgeProps {
  knowledges: Knowledge[] | Knowledge.SingleClass.Knowledge[];
  singleClass: SpuClass | Knowledge.SingleClass.Value;
  successCallback?: (ids: string[]) => void;
  placeholder?: ReactNode;
}
const ClosedKnowledge = memo((props: ClosedKnowledgeProps) => {
  const { t } = useTranslate();
  const { knowledges, singleClass, successCallback, placeholder = null } = props;
  const [visible, setVisible] = useState(false);
  const lang = useSystemLanguageValue();
  const singleClassName = useMemo(
    () => getMultiLangValue(singleClass.nameMultiLang, lang, singleClass.name),
    [singleClass, lang],
  );
  useEffect(() => {
    if (!visible) {
      setOpenIds([]);
    }
  }, [visible]);
  const [openIds, setOpenIds] = useState<string[]>([]);
  const { runAsync: openKnowledge, loading } = useRequest(
    (ids: string[]) =>
      updateKnowledgeStatus({
        openIds: ids,
        shutdownIds: [],
      }),
    {
      onSuccess: (_, params) => {
        successCallback?.(params[0]);
        setVisible(false);
      },
      onError: (knowledgeError) => {
        message.error(
          describeKnowledgeApiError(
            knowledgeError,
            undefined,
            t('translation.操作失败，请稍后再试'),
          ),
        );
      },
      manual: true,
    },
  );

  if (knowledges.length === 0) return <>{placeholder}</>;

  return (
    <>
      <JustRightHeightModal
        visible={visible}
        width={560}
        centered
        className={css`
          .ant-modal-body {
            padding: 0 !important;
            margin: 0 !important;
          }
        `}
        onCancel={() => setVisible(false)}
        okButtonProps={{
          loading,
        }}
        onOk={() => {
          openKnowledge(openIds);
        }}
        okText={t('phrase.保存')}
        title={t('未启用的知识点')}
      >
        <div className="bg-[var(--fett-surface-warning-bg)] px-[16px] text-[var(--fett-text-secondary)] py-[6px] leading-[20px] ">
          {t('未启用的知识点，机器人将不再使用该知识点下的内容进行任何商品的回复')}
        </div>
        <div className="px-[16px] min-h-[200px]">
          <div className="h-[22px] leading-[22px] mt-[13px] mb-[9px] flex items-center">
            <div className="w-[4px] h-[14px] bg-[#3370ff] mr-[8px]"></div>
            <div className="">{singleClassName}</div>
          </div>

          <div>
            {knowledges.map((each) => {
              const knowledgeName = getMultiLangValue(each.nameMultiLang, lang, each.knowledgeName);
              return (
                <div
                  key={each.id}
                  className="h-[40px] bg-[var(--fett-erp-card-bg)] rounded-[4px] flex justify-between mb-[8px] px-[16px] py-[8px]"
                >
                  <div className="flex-grow mr-[12px] truncate" title={knowledgeName}>
                    {knowledgeName}
                  </div>
                  <Switch
                    onChange={(checked) => {
                      if (checked) {
                        setOpenIds((prev) => {
                          return [...prev, each.id];
                        });
                      } else {
                        setOpenIds((prev) => {
                          return prev.filter((id) => id !== each.id);
                        });
                      }
                    }}
                    className="flex-shrink-0"
                    checked={openIds.includes(each.id)}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </JustRightHeightModal>
      <div
        onClick={() => setVisible(true)}
        className="!text-[var(--fett-text-secondary)] !font-normal cursor-pointer"
      >
        {t('未启用的知识点')}({get(knowledges, 'length', 0)})
      </div>
    </>
  );
});

ClosedKnowledge.displayName = 'ClosedKnowledge';

export default ClosedKnowledge;
